/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, OnInit} from '@angular/core';
import {UIRouter} from '@uirouter/core';
import * as _ from 'lodash';

import {OBJECT_TYPE} from '../../common/constants/object-type';
import {AdnShotterService} from '../../common/adn-shotter.service';
import {buildLastViewedEntries} from './simple-last-viewed.util';
import template from './transparency-shotter-page.component.html';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {deferMarkForCheck} from '../../common/util/defer-mark-for-check';
import {NavigationHistoryService} from '../../../components/util/navigation-history.service';
import {visiblePageNumbers} from '../../common/directives/pagination/visible-page-numbers';

@Component({
  standalone: false,
  selector: 'adn-transparency-shotter-page',
  template
})
export class TransparencyShotterPageComponent implements OnInit {
  lastViewed: any[] = [];
  lastViewedObject: any;

  device = 'DESKTOP';
  screenshots: any[];
  screenshotting = false;
  deleting = false;
  showScreenshots = false;
  loaded = true;
  takingScreenshot = false;
  takeSuccess = false;
  takeError = '';
  objectId: string;
  filterLineItemId: string;
  objectType: any;
  objectTypes = OBJECT_TYPE;
  resp: any;

  missingTransparencyShots: any[];
  fetchingMissingTransparency = false;
  showMissingTransparency = false;

  transparencyByLineItemMeta = {
    currentPage: 1,
    pageSize: 20,
    resolved: true
  };
  transparencyByLineItemPage: any;
  fetchingTransparencyByLineItem = false;
  showTransparencyByLineItem = false;

  constructor(
    @Inject(LocalUserProfile) localUserProfile: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly adnShotter: AdnShotterService,
    private readonly uiRouter: UIRouter,
    private readonly cdr: ChangeDetectorRef,
    navigationHistory: NavigationHistoryService
  ) {
    this.lastViewed = buildLastViewedEntries(localUserProfile, this.LocalNetworkProfile);
    this.lastViewedObject = _.get(this.lastViewed, [0]);
    this.objectId = navigationHistory.previousParams.id;
    this.objectType = _.find(_.values(OBJECT_TYPE), (ot) => ot.stateName === navigationHistory.previousState?.name);
  }

  ngOnInit(): void {
    const lineItemId = this.uiRouter.globals.params.lineItemId;
    if (lineItemId) {
      this.objectId = lineItemId;
      this.filterLineItemId = lineItemId;
      this.doCheck();
    }
  }

  doCheck(): void {
    if (!this.objectId) {
      this.takeSuccess = false;
      this.takeError = 'Enter a line item ID first.';
      return;
    }
    this.loaded = false;
    this.takingScreenshot = true;
    this.takeSuccess = false;
    this.takeError = '';
    this.resp = null;
    this.adnShotter.takeTransparency(this.objectId, this.device).then((data: any) => {
      this.resp = data;
      this.takeSuccess = true;
    }).catch((error: any) => {
      this.takeError = _.get(error, ['data', 'message'])
        || _.get(error, ['data'])
        || 'Unable to queue the transparency screenshot.';
      if (!_.isString(this.takeError)) {
        this.takeError = 'Unable to queue the transparency screenshot.';
      }
    }).finally(() => {
      this.loaded = true;
      this.takingScreenshot = false;
    });
  }

  refresh(): void {
    this.screenshotting = true;
    this.showScreenshots = true;
    this.adnShotter.fetchAllTransparency().then((data: any[]) => {
      // undefined → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferMarkForCheck(this.cdr, () => {
        this.screenshots = data;
      });
    }).finally(() => {
      this.screenshotting = false;
    });
  }

  refreshMissingTransparency(): void {
    this.fetchingMissingTransparency = true;
    this.showMissingTransparency = true;
    this.adnShotter.missingTransparency().then((data: any[]) => {
      // undefined → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferMarkForCheck(this.cdr, () => {
        this.missingTransparencyShots = data;
      });
    }).finally(() => {
      this.fetchingMissingTransparency = false;
    });
  }

  refreshTransparencyByLineItem(): void {
    this.fetchTransparencyByLineItem(this.transparencyByLineItemMeta.currentPage);
  }

  changeTransparencyByLineItemPage(page: number): void {
    if (page < 1 || page > this.transparencyByLineItemTotalPages() || page === this.transparencyByLineItemMeta.currentPage) {
      return;
    }
    this.transparencyByLineItemMeta.currentPage = page;
    this.fetchTransparencyByLineItem(page);
  }

  transparencyByLineItemTotalPages(): number {
    return Math.max(1, Math.ceil((this.transparencyByLineItemPage?.totalHits || 0) / this.transparencyByLineItemMeta.pageSize));
  }

  transparencyByLineItemPageNumbers(): number[] {
    return visiblePageNumbers(this.transparencyByLineItemMeta.currentPage, this.transparencyByLineItemTotalPages());
  }

  deleteScreenshot(screenshot: any, index: number): void {
    this.deleting = true;
    this.adnShotter.deleteScreenshot(screenshot).then(() => {
      _.pullAt(this.screenshots, index);
    }).finally(() => {
      this.deleting = false;
    });
  }

  private fetchTransparencyByLineItem(page?: number): void {
    this.fetchingTransparencyByLineItem = true;
    this.showTransparencyByLineItem = true;
    this.adnShotter.transparencyByLineItem(page || this.transparencyByLineItemMeta.currentPage, this.filterLineItemId).then((data: any) => {
      // undefined → page flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferMarkForCheck(this.cdr, () => {
        this.transparencyByLineItemMeta.currentPage = data.page;
        this.transparencyByLineItemMeta.pageSize = data.pageSize;
        this.transparencyByLineItemPage = {
          totalHits: data.totalCount,
          pageStart: data.totalCount ? ((data.page - 1) * data.pageSize) + 1 : 0,
          pageEnd: Math.min(data.totalCount, data.page * data.pageSize),
          items: _.map(data.results, (screenshots: any[], lineItemId: string) => ({
            lineItemId: lineItemId,
            lineItem: _.get(screenshots, [0, 'lineItem']),
            screenshots: screenshots
          }))
        };
      });
    }).finally(() => {
      this.fetchingTransparencyByLineItem = false;
    });
  }
}
